// src/components/ReportsPageContainer.tsx
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  gql,
  useLazyQuery,
  useMutation,
  useApolloClient,
} from "@apollo/client";
import { User } from "@/generated/graphql";
import { Container } from "@/components/ui/Container";
import { Typography } from "@/components/ui/Typography";
import { MenuItem } from "@/components/ui/MenuItem";
import { SectionCard } from "@/components/section-card/section-card";
import { ConfirmDialog } from "@/components/confirm-dialog/confirm-dialog";
import {
  ProminentButtonStyled,
  SelectStyled,
  PageWrapperStyled,
  ButtonGroupStyled,
} from "@/shared";
import {
  YearSelectStyled,
  HiddenFileInputStyled,
  FileRowStyled,
  FileNameStyled,
} from "./reports-page-style";

const GENERATE_REPORT = gql`
  query GenerateReport($year: Int!) {
    generateReport(year: $year)
  }
`;

const GENERATE_CSV_REPORT = gql`
  query GenerateCsvReport($year: Int!) {
    generateCsvReport(year: $year)
  }
`;

const GENERATE_DATA_EXPORT = gql`
  query GenerateDataExport {
    generateDataExport
  }
`;

const IMPORT_DATA = gql`
  mutation ImportData($payload: String!, $mode: ImportMode!) {
    importData(payload: $payload, mode: $mode) {
      categories
      subcategories
      expenses
      savingGoals
      investments
    }
  }
`;

// Trigger a browser download from a base64-encoded payload returned by the API.
const downloadBase64 = (base64: string, mime: string, filename: string) => {
  const link = document.createElement("a");
  link.href = `data:${mime};base64,${base64}`;
  link.download = filename;
  link.click();
};

export const ReportsPageContainer = ({ userData }: { userData: User }) => {
  const maxYear = new Date().getFullYear();
  const minYear = maxYear - 5;

  const [year, setYear] = useState(maxYear);

  const years = [];
  for (let y = maxYear; y >= minYear; y--) {
    years.push(y);
  }

  const [loadReport, { loading }] = useLazyQuery(GENERATE_REPORT, {
    fetchPolicy: "network-only",
    onError: (err) => toast.error(err.message),
    onCompleted: ({ generateReport }) => {
      downloadBase64(
        generateReport,
        "application/pdf",
        `MonthlyApp-BudgetReport-${year}.pdf`,
      );
    },
  });

  const [loadCsv, { loading: loadingCsv }] = useLazyQuery(GENERATE_CSV_REPORT, {
    fetchPolicy: "network-only",
    onError: (err) => toast.error(err.message),
    onCompleted: ({ generateCsvReport }) => {
      downloadBase64(
        generateCsvReport,
        "text/csv;charset=utf-8",
        `MonthlyApp-Expenses-${year}.csv`,
      );
    },
  });

  const [loadDataExport, { loading: loadingDataExport }] = useLazyQuery(
    GENERATE_DATA_EXPORT,
    {
      fetchPolicy: "network-only",
      onError: (err) => toast.error(err.message),
      onCompleted: ({ generateDataExport }) => {
        const today = new Date().toISOString().slice(0, 10);
        downloadBase64(
          generateDataExport,
          "application/json",
          `MonthlyApp-DataExport-${today}.json`,
        );
      },
    },
  );

  const client = useApolloClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);
  const [importingMode, setImportingMode] = useState<
    "MERGE" | "REPLACE" | null
  >(null);

  const [runImport, { loading: importing }] = useMutation(IMPORT_DATA, {
    onError: (err) => {
      setImportingMode(null);
      toast.error(err.message);
    },
    onCompleted: async ({ importData }) => {
      setImportingMode(null);
      toast.success(
        `Imported ${importData.expenses} expenses, ${importData.categories} categories, ${importData.savingGoals} saving goals, ${importData.investments} investments.`,
      );
      setFileName(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      try {
        await client.resetStore();
      } catch {
        // ignore refetch errors; the import itself already succeeded
      }
    },
  });

  const doImport = async (mode: "MERGE" | "REPLACE") => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    const payload = await file.text();
    setImportingMode(mode);
    runImport({ variables: { payload, mode } });
  };

  const requestImport = (mode: "MERGE" | "REPLACE") => {
    if (importing) return;
    if (!fileInputRef.current?.files?.[0]) {
      toast.error("Choose a JSON export file first.");
      return;
    }
    if (mode === "REPLACE") {
      setConfirmReplaceOpen(true);
      return;
    }
    doImport("MERGE");
  };

  const confirmReplace = () => {
    setConfirmReplaceOpen(false);
    doImport("REPLACE");
  };

  return (
    <Container>
      <PageWrapperStyled>
        <Typography variant="h5">Reports &amp; Data</Typography>

        <SectionCard
          title="Generate report"
          description="A summary of one year, as a PDF or a spreadsheet."
        >
          <YearSelectStyled>
            <SelectStyled
              id="year-select"
              label="Year"
              value={String(year)}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <MenuItem key={y} value={String(y)}>
                  {y}
                </MenuItem>
              ))}
            </SelectStyled>
          </YearSelectStyled>

          <ButtonGroupStyled>
            <ProminentButtonStyled
              onClick={() => loadReport({ variables: { year } })}
              textCenter
              outline
            >
              {loading ? "Generating…" : "Download PDF"}
            </ProminentButtonStyled>

            <ProminentButtonStyled
              onClick={() => loadCsv({ variables: { year } })}
              textCenter
              outline
            >
              {loadingCsv ? "Generating…" : "Download CSV"}
            </ProminentButtonStyled>
          </ButtonGroupStyled>
        </SectionCard>

        <SectionCard
          title="Export all data"
          description="A complete, re-importable copy of all your data (categories, expenses, saving goals, investments) as JSON."
        >
          <ButtonGroupStyled>
            <ProminentButtonStyled
              onClick={() => loadDataExport()}
              textCenter
              outline
            >
              {loadingDataExport ? "Exporting…" : "Download full data (JSON)"}
            </ProminentButtonStyled>
          </ButtonGroupStyled>
        </SectionCard>

        <SectionCard
          danger
          title="Import data"
          description="Restore from a JSON export. Merge adds new records and updates matching ones while keeping everything else. Replace all permanently deletes your current data first, then imports the file. Both run all-or-nothing."
        >
          <FileRowStyled>
            <ProminentButtonStyled
              onClick={() => fileInputRef.current?.click()}
              textCenter
              outline
            >
              Choose file
            </ProminentButtonStyled>
            <FileNameStyled>{fileName ?? "No file chosen"}</FileNameStyled>
            <HiddenFileInputStyled
              type="file"
              accept="application/json,.json"
              ref={fileInputRef}
              onChange={(e) => setFileName(e.target.files?.[0]?.name ?? null)}
            />
          </FileRowStyled>
          <ButtonGroupStyled>
            <ProminentButtonStyled
              onClick={() => requestImport("MERGE")}
              textCenter
              outline
              disabled={!fileName || importing}
            >
              {importingMode === "MERGE" ? "Importing…" : "Merge import"}
            </ProminentButtonStyled>
            <ProminentButtonStyled
              onClick={() => requestImport("REPLACE")}
              textCenter
              outline
              color="error"
              disabled={!fileName || importing}
            >
              {importingMode === "REPLACE" ? "Importing…" : "Replace all"}
            </ProminentButtonStyled>
          </ButtonGroupStyled>
        </SectionCard>

        <ConfirmDialog
          open={confirmReplaceOpen}
          title="Replace all data?"
          confirmLabel="Replace all"
          onConfirm={confirmReplace}
          onCancel={() => setConfirmReplaceOpen(false)}
        >
          This permanently deletes all your current categories, expenses, saving
          goals and investments, then imports the file. This{" "}
          <strong>cannot</strong> be undone.
        </ConfirmDialog>
      </PageWrapperStyled>
    </Container>
  );
};
